<script lang="ts" setup>
  import { ref } from 'vue'

  // Types
  interface ConfirmOptions {
    title: string
    message: string
    icon?: string
    confirmText?: string
    cancelText?: string
    confirmColor?: string
  }

  type ConfirmResolver = (value: boolean | PromiseLike<boolean>) => void

  // Reactive state
  const dialog = ref<boolean>(false)
  const confirmResolve = ref<ConfirmResolver | null>(null)
  const text = ref<string>('')
  const confirmTitle = ref<string>('')
  const confirmIcon = ref<string>('')
  const confirmText = ref<string>('Yes')
  const cancelText = ref<string>('No')
  const confirmColor = ref<string>('primary')

  // Methods
  const open = (
    title: string,
    message: string,
    icon: string = '',
    options: Partial<Pick<ConfirmOptions, 'confirmText' | 'cancelText' | 'confirmColor'>> = {}
  ): Promise<boolean> => {
    dialog.value = true
    confirmTitle.value = title
    text.value = message
    confirmIcon.value = icon
    confirmText.value = options.confirmText || 'Yes'
    cancelText.value = options.cancelText || 'No'
    confirmColor.value = options.confirmColor || 'primary'

    return new Promise<boolean>(resolve => {
      confirmResolve.value = resolve
    })
  }

  const agree = (): void => {
    if (confirmResolve.value) {
      confirmResolve.value(true)
      dialog.value = false
      confirmResolve.value = null
    }
  }

  const cancel = (): void => {
    if (confirmResolve.value) {
      confirmResolve.value(false)
      dialog.value = false
      confirmResolve.value = null
    }
  }

  const handleDialogClose = (): void => {
    if (confirmResolve.value) {
      cancel()
    }
  }

  // Expose methods for parent components
  defineExpose<{
    open: typeof open
  }>({
    open,
  })
</script>

<template>
  <v-dialog
    v-model="dialog"
    max-width="400"
    persistent
    @update:model-value="(value: boolean) => !value && handleDialogClose()"
  >
    <v-card
      class="confirm-dialog px-9 py-6"
      :prepend-icon="confirmIcon || undefined"
      rounded="xl"
    >
      <v-card-text class="pa-0 text-center">
        <h3 class="text-gray-100 font-weight-bold text-h6 mb-1">{{ confirmTitle }}</h3>
        <p class="text-grey-200 text-body-2">{{ text }}</p>
      </v-card-text>
      <v-card-actions class="mt-6 pa-0 d-flex justify-space-between flex-md-column ga-4">
        <v-btn
          :block="$vuetify.display.mdAndUp"
          border="grey-600 sm opacity-100"
          class="font-weight-bold py-2 px-4 flex-1-1-100"
          color="grey-100"
          height="44px"
          rounded="sm"
          variant="outlined"
          @click="cancel"
        >
          {{ cancelText }}
        </v-btn>

        <v-btn
          :block="$vuetify.display.mdAndUp"
          class="font-weight-bold py-2 px-4 flex-1-1-100"
          :color="confirmColor"
          flat
          height="44px"
          rounded="sm"
          variant="elevated"
          @click="agree"
        >
          {{ confirmText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
</style>
